import { useEffect, useRef } from 'react';
import { usePagination } from 'react-instantsearch';

/**
 * Internal component that automatically navigates to the last page of search
 * results on mount, unless an explicit `?page` param is present in the URL.
 *
 * This ensures that opening a forum thread always shows the most recent
 * comments (which appear on the last page, since results are sorted ascending
 * by creation date). When a user shares a direct link with `?page=X`, that
 * page is respected instead.
 *
 * Must be rendered inside an InstantSearch context.
 */
const GoToLastPage: React.FC = () => {
  const {
    nbPages,
    currentRefinement: currentPage,
    refine: setCurrentPage,
  } = usePagination();

  const hasNavigated = useRef(false);

  useEffect(() => {
    // Only navigate once per mount.
    if (hasNavigated.current) return;

    // Respect an explicit page param in the URL (e.g. a shared link to ?page=3).
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('page')) return;

    // Wait until the first search response has arrived.
    if (nbPages === 0) return;

    // Mark the decision as made so subsequent effect runs are skipped.
    hasNavigated.current = true;

    const lastPage = nbPages - 1; // usePagination uses 0-indexed pages.
    if (currentPage !== lastPage) {
      setCurrentPage(lastPage);
    }
  }, [nbPages, currentPage, setCurrentPage]);

  return null;
};

export default GoToLastPage;
